import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { NavItem } from './NavItem';
import { TopBar } from './TopBar';
import { DialogOverlayGlobal } from '@/components/ui/DialogOverlayGlobal';
import { OfflineIndicator } from '@/components/shared/OfflineIndicator';
import { AppLogo } from '@/components/shared/AppLogo';
import { MessageComposeDialog } from '@/components/email/MessageComposeDialog';
import { SampleDataBanner } from '@/components/banners/SampleDataBanner';
import { EntitlementInvalidBanner } from '@/components/shared/EntitlementInvalidBanner';
import { useDialogOverlay } from '@/stores/dialogOverlay.store';
import { AnimatePresence, motion } from 'framer-motion';
import { Sheet, SheetContent, SheetDescription, SheetTitle } from '@/components/ui/sheet';
import { TooltipProvider } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { useTeam } from '@/contexts/TeamContext';
import {
  Home,
  Car,
  Users,
  GitBranch,
  FileText,
  Receipt,
  BarChart3,
  Calendar,
  Wrench,
  Settings,
  HelpCircle,
  ShieldCheck,
  Package,
} from 'lucide-react';
import { usePlatformAdmin } from '@/hooks/useGlobalAdmin';
import { SessionTimeoutWarning } from '@/components/auth/SessionTimeoutWarning';
import { ActiveWorkBar } from '@/features/garage/components/time/ActiveWorkBar';
import { useSidebarResize } from '@/hooks/useSidebarResize';
import { useGarageEnabled } from '@/hooks/useGarageEnabled';
import { TeamSwitcher } from './TeamSwitcher';
import { SmartAddJobCenter } from '@/features/smartAdd/components/SmartAddJobCenter';

interface AppShellProps {
  children: React.ReactNode;
}

export function AppShell({ children }: AppShellProps) {
  const { t } = useTranslation('common');
  const { isVisible } = useDialogOverlay();
  const { isPlatformAdmin } = usePlatformAdmin();
  const garageEnabled = useGarageEnabled();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { team } = useTeam();

  // CONTRACT V2: Global inactive team guard - redirect to /team-inactive
  useEffect(() => {
    // Only redirect if team is inactive and not already on the inactive page
    if (team?.status === 'inactive' && location.pathname !== '/team-inactive') {
      navigate('/team-inactive', { replace: true });
    }
  }, [team?.status, location.pathname, navigate]);

  // Resizable sidebar with localStorage persistence
  const { width: sidebarWidth, isResizing, handleMouseDown } = useSidebarResize();

  const navigationItems = [
    { href: '/dashboard', icon: Home, labelKey: 'navigation.dashboard' },
    { href: '/vehicles', icon: Car, labelKey: 'navigation.vehicles' },
    { href: '/pipeline', icon: GitBranch, labelKey: 'navigation.pipeline' },
    { href: '/customers', icon: Users, labelKey: 'navigation.customers' },
    { href: '/inventory', icon: Package, labelKey: 'navigation.inventory' },
    { href: '/purchase-invoices', icon: FileText, labelKey: 'navigation.purchaseInvoices' },
    { href: '/sales-invoices', icon: Receipt, labelKey: 'navigation.salesInvoices' },
    { href: '/calendar', icon: Calendar, labelKey: 'navigation.calendar' },
    ...(garageEnabled ? [{ href: '/garage', icon: Wrench, labelKey: 'navigation.garage' }] : []),
    { href: '/analytics', icon: BarChart3, labelKey: 'navigation.analytics' },
  ];
  const bottomItems = [
    // Admin link - only shown to platform admins
    ...(isPlatformAdmin
      ? [{ href: '/admin/teams', icon: ShieldCheck, labelKey: 'navigation.admin' }]
      : []),
    { href: '/settings', icon: Settings, labelKey: 'navigation.settings' },
    { href: '/support', icon: HelpCircle, labelKey: 'navigation.support' },
  ];

  return (
    <>
      {/* Frosted blur overlay for header + sidebar - rendered in portal */}
      {typeof document !== 'undefined' &&
        createPortal(
          <AnimatePresence>
            {isVisible && (
              <motion.div
                key="frosted-blur"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.25 }}
                className="fixed top-0 left-0 w-full h-full pointer-events-none z-30 backdrop-blur-[3px]"
              />
            )}
          </AnimatePresence>,
          document.body
        )}

      {/* Main overlay for background + scroll lock - rendered in portal */}
      <DialogOverlayGlobal visible={isVisible} />

      {/* Offline/Online status indicator */}
      <OfflineIndicator />

      {/* Global Email Compose Dialog */}
      <MessageComposeDialog />

      <div className="relative flex h-full min-h-0 overflow-hidden bg-shell text-foreground">
        {/* Mobile Menu Sheet */}
        <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
          <SheetContent
            side="left"
            className="w-[260px] p-0 bg-sidebar-shell border-r border-sidebar-border"
          >
            <SheetTitle className="sr-only">Navigation Menu</SheetTitle>
            <SheetDescription className="sr-only">
              Main navigation menu for the application
            </SheetDescription>
            {/* Brand */}
            <div className="flex items-center gap-2 px-4 pt-6 pb-3 min-w-0">
              <AppLogo variant="icon" size="md" className="shrink-0" />
              <span className="font-semibold text-foreground flex-1 min-w-0 truncate">modcar</span>
            </div>
            <div className="px-3 pb-3">
              <TeamSwitcher />
            </div>

            {/* Main Navigation */}
            <nav className="flex-1 overflow-hidden px-3 py-4 space-y-1 text-[14px]">
              {navigationItems.map((item) => (
                <div key={item.href} onClick={() => setMobileMenuOpen(false)}>
                  <NavItem href={item.href} icon={item.icon} labelKey={item.labelKey} />
                </div>
              ))}
            </nav>

            {/* Bottom Items */}
            <div className="border-t border-surface-border p-3 space-y-1 text-[14px]">
              {bottomItems.map((item) => (
                <div key={item.href} onClick={() => setMobileMenuOpen(false)}>
                  <NavItem href={item.href} icon={item.icon} labelKey={item.labelKey} />
                </div>
              ))}
            </div>
          </SheetContent>
        </Sheet>

        {/* Desktop Sidebar - Resizable */}
        <TooltipProvider delayDuration={0}>
          <aside
            style={{ width: sidebarWidth }}
            className={cn(
              'hidden md:flex shrink-0 sticky top-0 h-screen overflow-hidden bg-sidebar-shell flex-col relative',
              'border-r border-sidebar-border'
            )}
          >
            {/* Brand */}
            <div className="pt-4 px-4 pb-2">
              <div className="flex items-center gap-2 min-w-0">
                <AppLogo variant="icon" size="md" className="shrink-0" />
                <span className="font-semibold text-foreground flex-1 min-w-0 truncate">
                  modcar
                </span>
              </div>
            </div>
            <div className="px-3 pb-2">
              <TeamSwitcher />
            </div>

            {/* Main Navigation */}
            <nav className="flex-1 overflow-hidden py-4 space-y-1 text-[14px] px-3">
              {navigationItems.map((item) => (
                <NavItem
                  key={item.href}
                  href={item.href}
                  icon={item.icon}
                  labelKey={item.labelKey}
                />
              ))}
            </nav>

            {/* Bottom Navigation */}
            <div className="mt-auto border-t border-surface-border py-3 space-y-1 text-[14px] px-3">
              {bottomItems.map((item) => (
                <NavItem
                  key={item.href}
                  href={item.href}
                  icon={item.icon}
                  labelKey={item.labelKey}
                />
              ))}
            </div>

            {/* Resize Handle - Finder-style */}
            <div
              onPointerDown={handleMouseDown}
              className={cn(
                'absolute top-0 right-0 h-full cursor-col-resize z-10',
                'hover:bg-primary/20',
                isResizing && 'bg-primary/30'
              )}
              style={{ width: 6, marginRight: -3 }}
              aria-label={t('sidebar.resize')}
              role="separator"
              aria-orientation="vertical"
            />
          </aside>
        </TooltipProvider>

        <div className="flex-1 min-h-0 flex flex-col min-h-[100dvh] overflow-hidden">
          {/* Full-bleed sticky TopBar - always visible at viewport width */}
          <header className="sticky top-0 inset-x-0 z-50 w-full bg-background/80 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b border-border isolation-auto">
            <TopBar onMenuClick={() => setMobileMenuOpen(true)} />
          </header>

          {/* Scrollable main content with proper containment */}
          <main className="flex-1 overflow-y-auto overflow-x-hidden scroll-smooth scrollbar-gutter-stable bg-shell">
            {/* Entitlement warning banner - shows when plan config is invalid */}
            <EntitlementInvalidBanner />
            {children}
          </main>
        </div>
      </div>

      {/* Session timeout warning dialog */}
      <SessionTimeoutWarning />

      {/* Active work timer bar - shows when user has running timer (garage-gated) */}
      {garageEnabled && <ActiveWorkBar />}

      {/* Sample data banner - shows for new teams with sample data */}
      <SampleDataBanner />

      {/* SmartAdd job center - global bottom-right progress indicator */}
      <SmartAddJobCenter />
    </>
  );
}
